import { Check, Copy, Download, Mail, MessageCircle, MessageSquare, Share2 } from "lucide-react";
import { useState } from "react";

import { useToast } from "../toast";
import { Dialog } from "../ui/dialog";

type Format = "og" | "story" | "square";
const FORMATS: { format: Format; label: string; ratio: string; file: string }[] = [
  { format: "og", label: "Carte du lien", ratio: "aspect-[1200/630] w-72", file: "lien" },
  { format: "story", label: "Story", ratio: "aspect-[9/16] w-36", file: "story" },
  { format: "square", label: "Post carré", ratio: "aspect-square w-52", file: "carre" },
];

function Visual({
  label,
  src,
  ratio,
  file,
}: {
  label: string;
  src: string;
  ratio: string;
  file: string;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <figure className="grid gap-2">
      <div className={`${ratio} relative overflow-hidden rounded-2xl bg-ink/5 ring-1 ring-line`}>
        {failed ? (
          <p className="grid size-full place-items-center p-3 text-center text-sm text-muted">
            Visuel indisponible
          </p>
        ) : (
          <img
            src={src}
            alt={`Visuel ${label}`}
            loading="lazy"
            className="absolute inset-0 size-full object-contain"
            onError={() => setFailed(true)}
          />
        )}
      </div>
      <figcaption className="flex items-center justify-between gap-2">
        <span className="font-bold">{label}</span>
        <a className="btn-ghost gap-1.5 text-sm" href={src} download={file}>
          <Download size={16} aria-hidden="true" />
          Télécharger
        </a>
      </figcaption>
    </figure>
  );
}

/** Rendered only while the dialog is open: it reads the page's origin, which the server has not. */
function ShareBody({
  path,
  title,
  text,
  card,
  slug,
}: {
  path: string;
  title: string;
  text: string;
  card: (format: Format) => string;
  slug: string;
}) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const url = new URL(path, window.location.origin).href;
  const message = `${text} ${url}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast("Lien copié");
    } catch {
      toast("Copiez le lien à la main : le navigateur a refusé.", "error");
    }
  };
  const nativeShare = async () => {
    try {
      await navigator.share({ title, text, url });
    } catch {
      // Cancelled by the person, or unsupported: nothing to report.
    }
  };

  return (
    <div className="grid gap-6">
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          className="input font-bold"
          readOnly
          value={url}
          aria-label="Lien de la page"
          onFocus={(event) => event.target.select()}
        />
        <button type="button" className="btn shrink-0 gap-2" onClick={() => void copy()}>
          {copied ? <Check size={18} aria-hidden="true" /> : <Copy size={18} aria-hidden="true" />}
          Copier
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {typeof navigator.share === "function" && (
          <button type="button" className="btn-secondary gap-2" onClick={() => void nativeShare()}>
            <Share2 size={18} aria-hidden="true" />
            Partager…
          </button>
        )}
        <a
          className="btn-secondary gap-2"
          href={`https://wa.me/?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener"
        >
          <MessageCircle size={18} aria-hidden="true" />
          WhatsApp
        </a>
        <a className="btn-secondary gap-2" href={`sms:?&body=${encodeURIComponent(message)}`}>
          <MessageSquare size={18} aria-hidden="true" />
          SMS
        </a>
        <a
          className="btn-secondary gap-2"
          href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(message)}`}
        >
          <Mail size={18} aria-hidden="true" />
          Email
        </a>
      </div>
      <div className="grid gap-3">
        <p className="text-muted">
          Les visuels portent le logo Listou. Publiez-les en story ou en post, puis ajoutez le lien.
        </p>
        <div className="flex flex-wrap items-end gap-6">
          {FORMATS.map((entry) => (
            <Visual
              key={entry.format}
              label={entry.label}
              src={card(entry.format)}
              ratio={entry.ratio}
              file={`listou-${slug}-${entry.file}.png`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * "Partager" on a page of the atlas: the link, the usual messengers, and the cards the
 * server draws for the page (the same picture a link preview shows).
 */
export function ShareButton({
  path,
  title,
  text,
  card,
  slug,
  tone = "light",
}: {
  /** Path and query of the page, resolved against the visitor's origin when sharing. */
  path: string;
  title: string;
  /** The sentence that goes with the link in a message. */
  text: string;
  card: (format: Format) => string;
  /** Names the downloaded files. */
  slug: string;
  /** "dark" for a button on a colored header. */
  tone?: "light" | "dark";
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${
          tone === "dark"
            ? "bg-white text-ink hover:bg-white/90"
            : "border border-line bg-surface hover:border-ink"
        }`}
      >
        <Share2 size={16} aria-hidden="true" />
        Partager
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Partager cette page" size="xl">
        <ShareBody path={path} title={title} text={text} card={card} slug={slug} />
      </Dialog>
    </>
  );
}
